"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_LAL_KITAB_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_LAL_KITAB_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorReportOfferCTA from "@/components/CalculatorReportOfferCTA";
import LalKitabKundliResults from "@/components/lal-kitab-kundli/LalKitabKundliResults";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import type { KundaliRequest } from "@/lib/api/kundali";
import {
  calculateLalKitabKundli,
  type LalKitabKundliRequest,
  type LalKitabKundliResponse,
} from "@/lib/api/lal-kitab";

type Language = "en" | "hi";
type SourcePath = "/lal-kitab-kundli" | "/hi/lal-kitab-kundli";

const COPY = {
  en: {
    title: "Free Lal Kitab Kundli Calculator",
    description:
      "Enter exact birth details to calculate house placements, active upay, and ancestral debts from the Lal Kitab engine.",
    formHeading: "Your birth details",
    submit: "Get My Lal Kitab Kundli",
    resultTitle: (name: string) => `${name}'s Lal Kitab Analysis`,
    reset: "Calculate Again",
    calculationError: "Something went wrong. Please try again.",
    trust: ["Pt. Roop Chand Joshi System", "Exact Birth Chart", "100% Free"],
    offerHeadline: "Why do the same money or family problems keep returning?",
    offerSubhead:
      "A remedy works when it matches the rin active in your chart. Get your Lal Kitab Remedy Report with active debts, blind planets, and chart-matched practical upay.",
  },
  hi: {
    title: "मुफ़्त लाल किताब कुंडली कैलकुलेटर",
    description:
      "सटीक जन्म विवरण से लाल किताब इंजन द्वारा भावों में ग्रह, सक्रिय उपाय और पैतृक ऋण जानें।",
    formHeading: "आपका जन्म विवरण",
    submit: "मेरी लाल किताब कुंडली बनाएं",
    resultTitle: (name: string) => `${name} का लाल किताब विश्लेषण`,
    reset: "फिर से गणना करें",
    calculationError: "कुछ गड़बड़ हुई। कृपया फिर से कोशिश करें।",
    trust: ["पं. रूप चंद जोशी पद्धति", "सटीक जन्म कुंडली", "100% मुफ़्त"],
    offerHeadline: "वही पैसे या परिवार की परेशानी बार-बार क्यों लौटती है?",
    offerSubhead:
      "उपाय तभी काम करता है जब वह आपकी कुंडली में सक्रिय ऋण से मेल खाए। सक्रिय ऋण, अंधे ग्रह और कुंडली-अनुसार व्यावहारिक उपायों के साथ अपनी लाल किताब रिपोर्ट पाएं।",
  },
} as const;

type LalKitabKundliClientProps = {
  crossLinks: ReactNode;
  language: Language;
  sourcePath: SourcePath;
};

export default function LalKitabKundliClient({
  crossLinks,
  language,
  sourcePath,
}: LalKitabKundliClientProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<LalKitabKundliResponse | null>(null);
  const [submittedBirth, setSubmittedBirth] = useState<LalKitabKundliRequest | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const copy = COPY[language];

  useLayoutEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
  }, [result]);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(undefined);
    setResult(null);
    try {
      const request = toLalKitabRequest(data);
      const response = await calculateLalKitabKundli(request);
      setSubmittedBirth(request);
      setResult(response);
      trackCalculatorUsed({
        calculator: "lal-kitab-kundli",
        language,
        source_path: sourcePath,
        has_time: true,
      });
    } catch (submitError: unknown) {
      setError(submitError instanceof Error ? submitError.message : copy.calculationError);
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResult(null);
    setSubmittedBirth(null);
    setError(undefined);
  }

  return (
    <div className="min-h-[calc(100vh-130px)] bg-surface-canvas">
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <LalKitabHeader language={language} />

        {!result ? (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              onSubmit={handleSubmit}
              loading={loading}
              error={error}
              heading={copy.formHeading}
              submitLabel={copy.submit}
              requireBirthTime
              requireResolvedPlace
              collectName
              language={language}
              analyticsToolName="lal-kitab-kundli"
              analyticsPlacement="lal_kitab_birth_details"
              rememberBirthDetails
              showTrustStrip
            />
          </div>
        ) : null}

        {result && submittedBirth ? (
          <div ref={resultRef}>
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="font-serif text-title font-bold text-text-primary">
                {copy.resultTitle(result.name)}
              </h2>
              <button
                type="button"
                onClick={reset}
                className="min-h-11 shrink-0 rounded-lg border border-border-default px-4 py-2 text-body-small font-semibold text-text-secondary"
              >
                {copy.reset}
              </button>
            </div>
            <LalKitabKundliResults
              result={result}
              language={language}
              resultOffer={
                <CalculatorReportOfferCTA
                  planId="lal_kitab_report"
                  language={language}
                  sourcePath={sourcePath}
                  placement={offerPlacement(language)}
                  customerProblem="lal_kitab"
                  headline={copy.offerHeadline}
                  subhead={copy.offerSubhead}
                  name={submittedBirth.name}
                  birthDate={submittedBirth.birth_date}
                  birthTime={submittedBirth.birth_time}
                  birthPlace={submittedBirth.birth_place}
                />
              }
            />
            {crossLinks}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function toLalKitabRequest(data: KundaliRequest): LalKitabKundliRequest {
  if (!data.birth_time) {
    throw new Error("Exact birth time is required to calculate Lal Kitab houses.");
  }
  if (data.latitude == null || data.longitude == null) {
    throw new Error("Select a birth city from the suggestions to calculate its coordinates.");
  }
  return {
    name: data.name,
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
  };
}

function offerPlacement(language: Language) {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_LAL_KITAB_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_LAL_KITAB_REPORT_BRIDGE;
}

function LalKitabHeader({ language }: { language: Language }) {
  const copy = COPY[language];
  return (
    <div className="mb-10 text-center">
      <p className="mb-3 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        लाल किताब · Lal Kitab
      </p>
      <h2 className="font-serif text-headline font-bold leading-tight text-text-primary sm:text-display">
        {copy.title}
      </h2>
      <p className="mx-auto mt-3 max-w-md text-body leading-relaxed text-text-secondary">
        {copy.description}
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-caption font-medium text-text-muted">
        <TrustSignal icon="shield" label={copy.trust[0]} />
        <TrustSignal icon="clock" label={copy.trust[1]} />
        <TrustSignal icon="user" label={copy.trust[2]} />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}
